import React from 'react';
import { useQuery } from '@apollo/react-hooks';
import PropTypes from 'prop-types';

import Gauge from '../../library/gauge/gauge';
import TireComparison from './components/tire-comparison/tire-comparison';
import {
  CloudShowersRegularIcon,
  SnowflakeRegularIcon,
  SunRegularIcon
} from '../../shared/icons/icons';

import { GET_ZIP_CODE_WEATHER_DATA } from '../../shared/graphql/form-queries';

import { useMyStore, useTreadwellForm } from '../../shared/hooks/hooks';

import { isValid5DigitPostalCode } from '../../shared/utils/validators/validators';

import './stopping-distance-info.scss';

const DEFAULT_WET_DISTANCE_ZIP_CODE = '67205';

function StoppingDistanceInfo(props) {
  const {
    dryDistance,
    dryRating,
    isLT = false,
    currentProductLabel,
    hideRecommended = false,
    hideWinterSection = false,
    precipitationDaysPerYear: precipitationDaysPerYearProp,
    rank,
    recommendedDryDistance,
    recommendedDryProductLabel,
    recommendedDryProductUrl,
    recommendedDryRank,
    recommendedWetDistance,
    recommendedWetProductLabel,
    recommendedWetProductUrl,
    recommendedWetRank,
    recommendedWetWornDistance,
    wetDistance,
    wetWornDistance,
    wetRating,
    winterDaysPerYear: winterDaysPerYearProp,
    winterRating
  } = props;

  const { myStore } = useMyStore();
  const { zipCodeValue: treadwellZipCode } = useTreadwellForm();
  const zipCode =
    treadwellZipCode ||
    myStore?.address?.postalCode?.replace(/[^0-9].+$/, '') ||
    DEFAULT_WET_DISTANCE_ZIP_CODE;

  const {
    data: weatherData,
    loading: weatherDataLoading,
    error: weatherDataError
  } = useQuery(GET_ZIP_CODE_WEATHER_DATA, {
    skip:
      !isValid5DigitPostalCode(zipCode) ||
      (Number.isFinite(precipitationDaysPerYearProp) &&
        Number.isFinite(winterDaysPerYearProp)),
    variables: {
      zipCode
    }
  });

  const {
    precipitationDaysPerYear = precipitationDaysPerYearProp,
    winterDaysPerYear = winterDaysPerYearProp
  } =
    weatherDataLoading || weatherDataError || !weatherData
      ? {}
      : weatherData?.recommendationQueries?.weatherDetails || {};

  const dryDaysPerYear = 365 - (winterDaysPerYear || 0);

  return (
    <div className="stopping-distance-info" styleName="container">
      <div
        className="stopping-distance-info__wet-dry-container"
        styleName="wet-dry-container"
      >
        <div styleName="wrapper-wet">
          <div styleName="climate">
            <CloudShowersRegularIcon styleName="rain-cloud-icon" />

            <div>
              <span styleName="climate-name">Wet Roads</span>
              <span styleName="climate-average">
                <span styleName="days">{precipitationDaysPerYear}</span>
                {` avg. days of rain in ${zipCode}`}
              </span>
            </div>
          </div>

          <TireComparison
            currentDistance={wetDistance}
            currentProductLabel={currentProductLabel}
            currentRank={rank}
            currentWornDistance={wetWornDistance}
            hideRecommended={hideRecommended}
            isLT={
              isLT ||
              Math.max(
                250,
                wetDistance,
                wetWornDistance,
                recommendedWetDistance,
                recommendedWetWornDistance
              ) > 250
            }
            isWet
            recommendedDistance={recommendedWetDistance}
            recommendedProductLabel={recommendedWetProductLabel}
            recommendedProductUrl={recommendedWetProductUrl}
            recommendedRank={recommendedWetRank}
            recommendedWornDistance={recommendedWetWornDistance}
          />

          <div styleName="sd-gauge">
            <Gauge
              draw
              fillPerc={wetRating / 5}
              gaugeColor="#3FB0FF"
              gaugeStrokeWidth={10}
              label="out of 5"
              value={wetRating}
              valuePos={{
                x: 125,
                y: 130
              }}
            />
          </div>
        </div>

        <div styleName="wrapper-dry">
          <div styleName="climate">
            <SunRegularIcon styleName="sun-icon" />

            <div>
              <span styleName="climate-name">Dry Roads</span>
              <span styleName="climate-average">
                <span styleName="days">{dryDaysPerYear}</span>
                {` avg. dry days in ${zipCode}`}
              </span>
            </div>
          </div>

          <TireComparison
            currentDistance={dryDistance}
            currentProductLabel={currentProductLabel}
            currentRank={rank}
            hideRecommended={hideRecommended}
            isDry
            isLT={
              isLT || Math.max(250, dryDistance, recommendedDryDistance) > 250
            }
            recommendedDistance={recommendedDryDistance}
            recommendedProductLabel={recommendedDryProductLabel}
            recommendedProductUrl={recommendedDryProductUrl}
            recommendedRank={recommendedDryRank}
          />

          <div styleName="sd-gauge">
            <Gauge
              draw
              fillPerc={dryRating / 5}
              gaugeColor="#FFA10D"
              gaugeStrokeWidth={10}
              label="out of 5"
              value={dryRating}
              valuePos={{
                x: 125,
                y: 130
              }}
            />
          </div>
        </div>
      </div>

      {!hideWinterSection && (
        <div
          className="stopping-distance-info__wrapper-winter"
          styleName="wrapper-winter"
        >
          <div
            className="stopping-distance-info__wrapper-details"
            styleName="winter-details"
          >
            <div styleName="climate">
              <SnowflakeRegularIcon styleName="snow-icon" />

              <div>
                <span styleName="climate-name">Winter Rating</span>
                <span styleName="climate-average">
                  <span styleName="days">{winterDaysPerYear}</span>
                  {` avg. winter days in ${zipCode}`}
                </span>
              </div>
            </div>

            <div styleName="winter-gauge">
              <Gauge
                draw
                fillPerc={winterRating / 5}
                gaugeColor="#667792"
                gaugeStrokeWidth={10}
                label="out of 5"
                value={winterRating}
                valuePos={{
                  x: 125,
                  y: 130
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

StoppingDistanceInfo.propTypes = {
  currentProductLabel: PropTypes.string,
  dryDistance: PropTypes.number,
  dryRating: PropTypes.number,
  hideRecommended: PropTypes.bool,
  hideWinterSection: PropTypes.bool,
  isLT: PropTypes.bool,
  precipitationDaysPerYear: PropTypes.number,
  rank: PropTypes.number,
  recommendedDryDistance: PropTypes.number,
  recommendedDryProductLabel: PropTypes.string,
  recommendedDryProductUrl: PropTypes.string,
  recommendedDryRank: PropTypes.number,
  recommendedWetDistance: PropTypes.number,
  recommendedWetProductLabel: PropTypes.string,
  recommendedWetProductUrl: PropTypes.string,
  recommendedWetRank: PropTypes.number,
  recommendedWetWornDistance: PropTypes.number,
  wetDistance: PropTypes.number,
  wetRating: PropTypes.number,
  wetWornDistance: PropTypes.number,
  winterDaysPerYear: PropTypes.number,
  winterRating: PropTypes.number,
  zipCode: PropTypes.string
};

export default StoppingDistanceInfo;
